import type { OmniScope } from "./oauth.js";
import { intakeUploadContent } from "./intake-formats.js";

// ---------- Error shape ----------

/**
 * Standard API error payload returned by Omni v1 REST routes and MCP tools.
 */
export interface ApiError {
  error: string;
}

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface RouteParameterDescriptor {
  name: string;
  in: "path" | "query" | "header";
  required: boolean;
  description: string;
  schema: Record<string, unknown>;
}

export interface RouteRequestBodyDescriptor {
  required: boolean;
  description?: string;
  content: Record<string, { schema: Record<string, unknown> }>;
}

export interface RouteResponseDescriptor {
  description: string;
  content?: Record<string, { schema: Record<string, unknown> }>;
}

export interface V1RouteDescriptor {
  method: HttpMethod;
  /** Express route path pattern with :param placeholders */
  path: string;
  /** OpenAPI 3.1 path pattern with {param} placeholders */
  openapiPath: string;
  operationId: string;
  summary: string;
  description: string;
  /** Required OAuth scope. null indicates public route (e.g. openapi.json) */
  requiredScope: OmniScope | null;
  /** True if the route modifies resources (customers, contacts, quote drafts) */
  writeOperation: boolean;
  parameters?: readonly RouteParameterDescriptor[];
  requestBody?: RouteRequestBodyDescriptor;
  responses: Readonly<Record<string, RouteResponseDescriptor>>;
}

// ---------- REST v1 Route Table ----------

export const V1_ROUTES: readonly V1RouteDescriptor[] = [
  {
    method: "GET",
    path: "/api/v1/me",
    openapiPath: "/api/v1/me",
    operationId: "getMe",
    summary: "Current authenticated identity",
    description: "Get the current authenticated user and dealer identity.",
    requiredScope: "omni.read",
    writeOperation: false,
    responses: {
      "200": {
        description: "Authenticated user and optional dealer profile",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/Me" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/customers",
    openapiPath: "/api/v1/customers",
    operationId: "listCustomers",
    summary: "List customers",
    description: "List customer summaries scoped to the caller's dealer, optionally filtered by text query.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "q",
        in: "query",
        required: false,
        description: "Search filter matching customer name, phone number, or Autotask company ID",
        schema: { type: "string" },
      },
    ],
    responses: {
      "200": {
        description: "Array of customer summaries",
        content: {
          "application/json": {
            schema: {
              type: "array",
              items: { $ref: "#/components/schemas/CustomerSummary" },
            },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/customers/:id",
    openapiPath: "/api/v1/customers/{id}",
    operationId: "getCustomer",
    summary: "Get customer detail",
    description: "Get complete customer detail including contacts and billing links by Autotask company ID.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Autotask company ID of the customer",
        schema: { type: "integer" },
      },
    ],
    responses: {
      "200": {
        description: "Customer detail with contacts and billing state",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/CustomerV2" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Customer not found or outside caller scope",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "POST",
    path: "/api/v1/customers",
    openapiPath: "/api/v1/customers",
    operationId: "createCustomer",
    summary: "Create customer",
    description: "Create a new prospect customer company in Autotask with primary contact and allocated account number.",
    requiredScope: "omni.write",
    writeOperation: true,
    requestBody: {
      required: true,
      description: "Customer prospect parameters with address and contact",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/CustomerInput" },
        },
      },
    },
    responses: {
      "201": {
        description: "Created customer summary with contacts",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/CustomerSummary" },
          },
        },
      },
      "400": {
        description: "Validation failure or missing required fields",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Token lacks omni.write scope",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "POST",
    path: "/api/v1/customers/:id/contacts",
    openapiPath: "/api/v1/customers/{id}/contacts",
    operationId: "addCustomerContact",
    summary: "Add customer contact",
    description: "Add a contact person to an existing Autotask customer.",
    requiredScope: "omni.write",
    writeOperation: true,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Autotask company ID of the customer",
        schema: { type: "integer" },
      },
    ],
    requestBody: {
      required: true,
      description: "Contact input fields",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/ContactInput" },
        },
      },
    },
    responses: {
      "201": {
        description: "Created customer contact",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/CustomerContact" },
          },
        },
      },
      "400": {
        description: "Invalid contact fields",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Token lacks omni.write scope",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Customer not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "409": {
        description: "Contact with this email already exists on the company",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/catalog",
    openapiPath: "/api/v1/catalog",
    operationId: "listCatalog",
    summary: "List catalog products",
    description: "List quotable catalog products with prices and floor prices available to the caller's dealer.",
    requiredScope: "omni.read",
    writeOperation: false,
    responses: {
      "200": {
        description: "List of quotable products",
        content: {
          "application/json": {
            schema: {
              type: "array",
              items: { $ref: "#/components/schemas/PricedProduct" },
            },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/quote-templates",
    openapiPath: "/api/v1/quote-templates",
    operationId: "listQuoteTemplates",
    summary: "List quote templates",
    description: "List reusable quote templates configured for the caller's dealer.",
    requiredScope: "omni.read",
    writeOperation: false,
    responses: {
      "200": {
        description: "List of quote templates",
        content: {
          "application/json": {
            schema: {
              type: "array",
              items: { $ref: "#/components/schemas/QuoteTemplateDetail" },
            },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/quotes",
    openapiPath: "/api/v1/quotes",
    operationId: "listQuotes",
    summary: "List quotes",
    description: "List quotes within the caller's scope, optionally filtered by status.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "status",
        in: "query",
        required: false,
        description: "Filter by quote status",
        schema: {
          type: "string",
          enum: ["draft", "needs_approval", "sent", "viewed", "signed", "ordered", "live", "lost"],
        },
      },
    ],
    responses: {
      "200": {
        description: "Array of quotes",
        content: {
          "application/json": {
            schema: {
              type: "array",
              items: { $ref: "#/components/schemas/Quote" },
            },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/quotes/:id",
    openapiPath: "/api/v1/quotes/{id}",
    operationId: "getQuote",
    summary: "Get quote",
    description: "Get full details of a specific quote by ID.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Quote ID (e.g. Q-1001)",
        schema: { type: "string" },
      },
    ],
    responses: {
      "200": {
        description: "Quote record",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/Quote" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Quote not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "POST",
    path: "/api/v1/quotes",
    openapiPath: "/api/v1/quotes",
    operationId: "createQuoteDraft",
    summary: "Create quote draft",
    description: "Create a new quote draft. Quotes created through API are always drafts and require review in Omni.",
    requiredScope: "omni.write",
    writeOperation: true,
    requestBody: {
      required: true,
      description: "Quote creation parameters",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/QuoteInput" },
        },
      },
    },
    responses: {
      "201": {
        description: "Created quote draft",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/Quote" },
          },
        },
      },
      "400": {
        description: "Validation error or unquotable product",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Token lacks omni.write scope",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "PATCH",
    path: "/api/v1/quotes/:id",
    openapiPath: "/api/v1/quotes/{id}",
    operationId: "updateQuoteDraft",
    summary: "Update quote draft",
    description: "Update an existing quote draft. Modifying quotes in sent, signed, or ordered status is forbidden.",
    requiredScope: "omni.write",
    writeOperation: true,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Quote ID to update",
        schema: { type: "string" },
      },
    ],
    requestBody: {
      required: true,
      description: "Quote fields to update",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/QuotePatchInput" },
        },
      },
    },
    responses: {
      "200": {
        description: "Updated quote",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/Quote" },
          },
        },
      },
      "400": {
        description: "Validation error or invalid status transition",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Token lacks omni.write scope",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Quote not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/quotes/:id/pdf",
    openapiPath: "/api/v1/quotes/{id}/pdf",
    operationId: "getQuotePdf",
    summary: "Download quote PDF",
    description: "Download the generated PDF document for a quote.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Quote ID",
        schema: { type: "string" },
      },
    ],
    responses: {
      "200": {
        description: "Quote PDF document binary",
        content: {
          "application/pdf": {
            schema: {
              type: "string",
              format: "binary",
            },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Quote not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/orders",
    openapiPath: "/api/v1/orders",
    operationId: "listOrders",
    summary: "List orders",
    description: "List orders within caller scope, optionally filtered by status.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "status",
        in: "query",
        required: false,
        description: "Filter by order status",
        schema: {
          type: "string",
          enum: ["in_progress", "awaiting_billing_approval", "needs_attention", "complete"],
        },
      },
    ],
    responses: {
      "200": {
        description: "Array of orders",
        content: {
          "application/json": {
            schema: {
              type: "array",
              items: { $ref: "#/components/schemas/Order" },
            },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/orders/:id",
    openapiPath: "/api/v1/orders/{id}",
    operationId: "getOrder",
    summary: "Get order",
    description: "Get full details of a specific order by ID.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Order ID (e.g. O-1001)",
        schema: { type: "string" },
      },
    ],
    responses: {
      "200": {
        description: "Order record",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/Order" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Order not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "PATCH",
    path: "/api/v1/orders/:id",
    openapiPath: "/api/v1/orders/{id}",
    operationId: "setOrderLineNumbers",
    summary: "Set each line's Telephone Number",
    description:
      "Set the Telephone Number on each order line whose OneBill plan needs one, as the seller. Phoneware confirms them at billing approval. Each entry sets its line's number as 10 digits; an empty number takes it off, and lines not listed keep theirs. Refused once billing is approved.",
    requiredScope: "omni.write",
    writeOperation: true,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Order ID (e.g. O-1001)",
        schema: { type: "string" },
      },
    ],
    requestBody: {
      required: true,
      description: "The order's updatedAt and the numbers to set",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/OrderLineNumbersInput" },
        },
      },
    },
    responses: {
      "200": {
        description: "The order with each line's Telephone Number",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/Order" },
          },
        },
      },
      "400": {
        description: "A number that isn't 10 digits, on a line that doesn't take one, or a field the API doesn't set; the error names the line",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/LineNumbersError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Token lacks omni.write scope",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Order not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "409": {
        description: "The order changed since expectedUpdatedAt, billing is already approved, or the order is complete",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "502": {
        description: "OneBill can't say which lines need a Telephone Number",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "POST",
    path: "/api/v1/quote-intakes",
    openapiPath: "/api/v1/quote-intakes",
    operationId: "uploadQuoteIntake",
    summary: "Upload quote document",
    description: "Upload a quote document as raw PDF for extraction and catalog matching.",
    requiredScope: "omni.write",
    writeOperation: true,
    parameters: [
      {
        name: "X-Filename",
        in: "header",
        required: false,
        description: "Original filename of the uploaded PDF",
        schema: { type: "string" },
      },
      {
        name: "dealerId",
        in: "query",
        required: false,
        description: "Target dealer ID (ops callers only)",
        schema: { type: "string" },
      },
    ],
    requestBody: {
      required: true,
      description: "Raw quote file bytes",
      content: intakeUploadContent(),
    },
    responses: {
      "201": {
        description: "Created quote intake record",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/QuoteIntakeView" },
          },
        },
      },
      "400": {
        description: "Bad request or missing required parameters",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Forbidden: insufficient scope or permissions",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "413": {
        description: "File exceeds 10MB limit",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "415": {
        description: "Invalid document content type. Expected application/pdf",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "POST",
    path: "/api/v1/quote-intakes/text",
    openapiPath: "/api/v1/quote-intakes/text",
    operationId: "submitQuoteIntakeText",
    summary: "Submit pasted quote text",
    description: "Submit pasted quote text or a conversation transcript for extraction and catalog matching.",
    requiredScope: "omni.write",
    writeOperation: true,
    requestBody: {
      required: true,
      description: "Pasted text and kind",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/QuoteIntakeTextRequest" },
        },
      },
    },
    responses: {
      "201": {
        description: "Created quote intake",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/QuoteIntakeView" },
          },
        },
      },
      "200": {
        description: "Existing intake for the same source",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/QuoteIntakeView" },
          },
        },
      },
      "400": {
        description: "Text is required, textKind is invalid, or the text could not be parsed",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Forbidden: insufficient scope or permissions",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "413": {
        description: "Text is over 200,000 characters",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "POST",
    path: "/api/v1/quote-intakes/kqm",
    openapiPath: "/api/v1/quote-intakes/kqm",
    operationId: "importKqmQuote",
    summary: "Import a KQM quote",
    description: "Import a KQM quote by link, id, or quote number. Phoneware staff only.",
    requiredScope: "omni.write",
    writeOperation: true,
    requestBody: {
      required: true,
      description: "KQM quote reference",
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/QuoteIntakeKqmRequest" },
        },
      },
    },
    responses: {
      "201": {
        description: "Created quote intake",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/QuoteIntakeView" },
          },
        },
      },
      "200": {
        description: "Existing intake for the same source",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/QuoteIntakeView" },
          },
        },
      },
      "400": {
        description: "The quote reference could not be parsed, or KQM has more than one quote with that number",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "403": {
        description: "Only Phoneware staff can import KQM quotes",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "KQM has no quote for that reference",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/quote-intakes/:id",
    openapiPath: "/api/v1/quote-intakes/{id}",
    operationId: "getQuoteIntake",
    summary: "Get quote intake",
    description: "Get details, extraction results, matching lines, and status for a quote intake document.",
    requiredScope: "omni.read",
    writeOperation: false,
    parameters: [
      {
        name: "id",
        in: "path",
        required: true,
        description: "Quote intake unique identifier",
        schema: { type: "string" },
      },
    ],
    responses: {
      "200": {
        description: "Quote intake details",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/QuoteIntakeView" },
          },
        },
      },
      "401": {
        description: "Missing or invalid bearer token",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
      "404": {
        description: "Quote intake not found",
        content: {
          "application/json": {
            schema: { $ref: "#/components/schemas/ApiError" },
          },
        },
      },
    },
  },
  {
    method: "GET",
    path: "/api/v1/openapi.json",
    openapiPath: "/api/v1/openapi.json",
    operationId: "getOpenApiJson",
    summary: "OpenAPI specification document",
    description: "Returns the OpenAPI 3.1 specification document describing the Omni Dealer REST API v1.",
    requiredScope: null,
    writeOperation: false,
    responses: {
      "200": {
        description: "OpenAPI 3.1 document as JSON data",
        content: {
          "application/json": {
            schema: {
              type: "object",
              description: "OpenAPI specification document",
            },
          },
        },
      },
    },
  },
] as const;

// ---------- Pure route helper functions ----------

export function normalizeRoutePath(path: string): string {
  return path.replace(/:([a-zA-Z0-9_]+)/g, "{$1}");
}

export function findV1Route(method: string, path: string): V1RouteDescriptor | undefined {
  const normMethod = method.toUpperCase();
  const normPath = normalizeRoutePath(path);
  return V1_ROUTES.find((r) => r.method === normMethod && (r.path === path || r.openapiPath === normPath));
}

export function isInternalRoute(path: string): boolean {
  const clean = path.split("?")[0].trim();
  return clean.startsWith("/api/internal/") || clean === "/api/internal";
}

export function checkRouteAccess(
  route: V1RouteDescriptor,
  grantedScopes: readonly string[],
): { ok: true } | { ok: false; status: 401 | 403; error: string } {
  if (route.requiredScope === null) {
    return { ok: true };
  }
  if (!grantedScopes || grantedScopes.length === 0) {
    return { ok: false, status: 401, error: "Authentication required." };
  }
  if (!grantedScopes.includes(route.requiredScope)) {
    return {
      ok: false,
      status: 403,
      error: `Forbidden: this route requires scope '${route.requiredScope}'.`,
    };
  }
  return { ok: true };
}
